import { cn } from "@/lib/utils";

interface SkeletonProps {
  className?: string;
}

/** 기본 스켈레톤 블록 */
export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      className={cn("animate-pulse rounded-lg bg-gray-200", className)}
      role="status"
      aria-label="로딩 중"
    />
  );
}

/** 텍스트 줄 스켈레톤 */
export function SkeletonLine({ className }: SkeletonProps) {
  return <Skeleton className={cn("h-4 w-full", className)} />;
}

/** 원형 스켈레톤 (아바타 등) */
export function SkeletonCircle({ className }: SkeletonProps) {
  return <Skeleton className={cn("h-10 w-10 rounded-full", className)} />;
}

/** 카드 형태 스켈레톤 */
export function SkeletonCard({ className }: SkeletonProps) {
  return (
    <div
      className={cn("animate-pulse space-y-3 rounded-2xl border border-gray-100 p-5", className)}
    >
      <Skeleton className="h-5 w-2/5" />
      <div className="flex gap-1.5">
        <Skeleton className="h-5 w-14 rounded-full" />
        <Skeleton className="h-5 w-14 rounded-full" />
      </div>
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
    </div>
  );
}

/** 채팅 메시지 스켈레톤 */
export function SkeletonChatMessage({ isUser = false }: { isUser?: boolean }) {
  return (
    <div className={cn("flex gap-3 px-4 py-3", isUser ? "justify-end" : "")}>
      {!isUser && <SkeletonCircle className="h-8 w-8 shrink-0" />}
      <div
        className={cn(
          "space-y-2",
          isUser ? "items-end" : "",
          isUser ? "max-w-[60%]" : "max-w-[75%] flex-1",
        )}
      >
        <Skeleton className={cn("h-4", isUser ? "ml-auto w-40" : "w-full")} />
        {!isUser && <Skeleton className="h-4 w-3/4" />}
        {!isUser && <Skeleton className="h-4 w-1/2" />}
      </div>
    </div>
  );
}

/** 채팅 페이지 전체 스켈레톤 */
export function ChatPageSkeleton() {
  return (
    <div className="flex h-[calc(100dvh-3.5rem)]" role="status" aria-label="채팅 페이지 로딩 중">
      <div className="flex min-w-0 flex-1 flex-col">
        {/* 상단 바 */}
        <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-2.5">
          <Skeleton className="h-8 w-8 rounded-lg" />
          <div className="flex items-center gap-2">
            <SkeletonCircle className="h-6 w-6" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>

        {/* 메시지 영역 */}
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6">
          <Skeleton className="h-16 w-16 rounded-2xl" />
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-36 rounded-2xl" />
            ))}
          </div>
        </div>

        {/* 입력 영역 */}
        <div className="border-t border-gray-100 px-4 pt-2 pb-3">
          <Skeleton className="h-12 w-full rounded-xl" />
          <Skeleton className="mx-auto mt-1.5 h-3 w-48" />
        </div>
      </div>
    </div>
  );
}

/** 시술 목록 스켈레톤 */
export function ProcedureListSkeleton() {
  return (
    <div
      className="grid grid-cols-1 gap-3 pb-8 sm:grid-cols-2 lg:grid-cols-3"
      role="status"
      aria-label="목록 로딩 중"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

/** 풀 페이지 로딩 스켈레톤 */
export function PageSkeleton() {
  return (
    <div
      className="mx-auto max-w-3xl animate-pulse px-5 pt-6 pb-20"
      role="status"
      aria-label="페이지 로딩 중"
    >
      <Skeleton className="h-8 w-48" />
      <Skeleton className="mt-2 h-4 w-72" />
      <div className="mt-8 space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** 폼 페이지 스켈레톤 (로그인/회원가입 등) */
export function FormSkeleton() {
  return (
    <div
      className="mx-auto max-w-sm animate-pulse px-5 py-12"
      role="status"
      aria-label="폼 로딩 중"
    >
      <Skeleton className="mx-auto mb-2 h-8 w-32" />
      <Skeleton className="mx-auto mb-8 h-4 w-48" />
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i}>
            <Skeleton className="mb-1.5 h-3 w-16" />
            <Skeleton className="h-12 w-full rounded-xl" />
          </div>
        ))}
        <Skeleton className="mt-2 h-12 w-full rounded-full" />
      </div>
    </div>
  );
}
